import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Printer, Plus, Search, Star, Edit, Trash2, Copy, Eye, Settings
} from 'lucide-react'
import cuponsTermicosService, {
  CupomTermico,
  getTipoCupomLabel,
  getStatusCupom,
  tiposCupomTermico
} from '../services/templates'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import Modal from '../components/Modal'
import CupomTermicoPreview from '../components/CupomTermicoPreview'
import { useRequireAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'

const CuponsTermicosPage: React.FC = () => {
  // Proteger página para APENAS ADMIN
  useRequireAuth(['ADMIN'])

  const { success, error: showError } = useToast()

  const [cupomsByType, setCupomsByType] = useState<Record<string, CupomTermico[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCupom, setSelectedCupom] = useState<CupomTermico | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  useEffect(() => {
    loadCuponsGrouped()
  }, [])

  const loadCuponsGrouped = async () => {
    try {
      setLoading(true)
      setError(null)
      const grouped = await cuponsTermicosService.listGrouped()
      setCupomsByType(grouped)
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar cupons térmicos')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (cupom: CupomTermico) => {
    try {
      await cuponsTermicosService.delete(cupom.id)
      success('Cupom térmico removido com sucesso')
      loadCuponsGrouped()
      setShowDeleteModal(false)
      setSelectedCupom(null)
    } catch (err: any) {
      showError('Erro ao remover cupom térmico', err.message)
    }
  }

  const handleDuplicate = async (cupom: CupomTermico) => {
    try {
      await cuponsTermicosService.duplicate(cupom.id)
      success('Cupom térmico duplicado com sucesso')
      loadCuponsGrouped()
    } catch (err: any) {
      showError('Erro ao duplicar cupom térmico', err.message)
    }
  }

  const handleSetPadrao = async (cupom: CupomTermico) => {
    try {
      await cuponsTermicosService.setPadrao(cupom.id)
      success('Cupom térmico definido como padrão')
      loadCuponsGrouped()
    } catch (err: any) {
      showError('Erro ao definir cupom térmico como padrão', err.message)
    }
  }

  // Filtrar cupons baseado na busca
  const filteredCupomsByType = Object.entries(cupomsByType || {}).reduce((acc, [tipo, cupons]) => {
    if (searchTerm) {
      const filtered = cupons.filter(cupom =>
        cupom.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cupom.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      if (filtered.length > 0) {
        acc[tipo] = filtered
      }
    } else {
      acc[tipo] = cupons
    }
    return acc
  }, {} as Record<string, CupomTermico[]>)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <Button
          onClick={loadCuponsGrouped}
          className="mt-2"
          variant="outline"
        >
          Tentar novamente
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cupons Térmicos</h1>
          <p className="text-gray-600">
            Gerencie os modelos de cupons para impressora térmica
          </p>
        </div>
        <Link to="/cupons-termicos/novo">
          <Button leftIcon={<Plus className="h-4 w-4" />}>
            Novo Cupom
          </Button>
        </Link>
      </div>

      {/* Barra de busca */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Buscar cupons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de cupons agrupados por tipo */}
      <div className="space-y-8">
        {Object.keys(filteredCupomsByType).length === 0 ? (
          <div className="text-center py-12">
            <Printer className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum cupom encontrado' : 'Nenhum cupom cadastrado'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm
                ? 'Tente alterar os termos da busca'
                : 'Comece criando seu primeiro modelo de cupom térmico'
              }
            </p>
            {!searchTerm && (
              <Link to="/cupons-termicos/novo">
                <Button leftIcon={<Plus className="h-4 w-4" />}>
                  Criar Primeiro Cupom
                </Button>
              </Link>
            )}
          </div>
        ) : (
          tiposCupomTermico.map(({ value: tipo, label }) => {
            const cupons = filteredCupomsByType[tipo]
            if (!cupons || cupons.length === 0) return null

            return (
              <div key={tipo} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">{label}</h2>
                  <p className="text-sm text-gray-600">
                    {cupons.length} cupom{cupons.length !== 1 ? 's' : ''}
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cupons.map((cupom) => (
                      <div
                        key={cupom.id}
                        className="border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        {/* Preview da configuração */}
                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Printer className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {cupom.configuracao.larguraPapel}mm
                            </span>
                            {cupom.padrao && (
                              <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            )}
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div>Fonte: {cupom.configuracao.tamanhoFonte}</div>
                            <div className="flex gap-3">
                              <span className={cupom.configuracao.cortarPapel ? 'text-green-600' : 'text-gray-400'}>
                                Corta papel
                              </span>
                              <span className={cupom.configuracao.abrirGaveta ? 'text-green-600' : 'text-gray-400'}>
                                Abre gaveta
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Informações do cupom */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-medium text-gray-900">{cupom.nome}</h3>
                              {cupom.descricao && (
                                <p className="text-sm text-gray-600 mt-1">{cupom.descricao}</p>
                              )}
                            </div>
                            <Badge
                              variant={getStatusCupom(cupom.ativo).color as any}
                              text={getStatusCupom(cupom.ativo).label}
                            />
                          </div>

                          <div className="text-xs text-gray-500 mb-4">
                            <div>Criado por: {cupom.criador.nome}</div>
                            <div>Versão: {cupom.versao}</div>
                            <div>
                              Atualizado: {new Date(cupom.updatedAt).toLocaleDateString('pt-PT')}
                            </div>
                          </div>

                          {/* Ações */}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedCupom(cupom)
                                setShowPreviewModal(true)
                              }}
                              leftIcon={<Eye className="h-3 w-3" />}
                            >
                              Preview
                            </Button>

                            <Link to={`/cupons-termicos/${cupom.id}/editar`}>
                              <Button
                                size="sm"
                                variant="outline"
                                leftIcon={<Edit className="h-3 w-3" />}
                              >
                                Editar
                              </Button>
                            </Link>

                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDuplicate(cupom)}
                                title="Duplicar"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>

                              {!cupom.padrao && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSetPadrao(cupom)}
                                  title="Definir como padrão"
                                >
                                  <Star className="h-3 w-3" />
                                </Button>
                              )}

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedCupom(cupom)
                                  setShowDeleteModal(true)
                                }}
                                title="Remover"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Exclusão"
      >
        {selectedCupom && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja remover o cupom térmico <strong>{selectedCupom.nome}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDelete(selectedCupom)}
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                Remover
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de preview */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Preview do Cupom"
        size="lg"
      >
        {selectedCupom && (
          <CupomTermicoPreview
            cupom={selectedCupom}
            dados={{
              id: 1,
              tipo: selectedCupom.tipo,
              dadosEmpresa: {
                nome: 'NexaMakers OS',
                cnpj: '00.000.000/0001-00',
                endereco: 'Rua exemplo, 123 - Centro',
                telefone: '(11) 1234-5678',
                email: 'contato@nexamakers.com'
              },
              dadosCliente: {
                nome: 'Cliente Exemplo',
                telefone: '(11) 9876-5432',
                email: 'cliente@email.com',
                documento: '123.456.789-00'
              },
              itens: selectedCupom.tipo === 'ORCAMENTO' || selectedCupom.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? [
                {
                  descricao: 'Troca de tela',
                  quantidade: 1,
                  valorUnitario: 150.00,
                  valorTotal: 150.00
                },
                {
                  descricao: 'Mão de obra',
                  valorTotal: 50.00
                }
              ] : undefined,
              valorTotal: selectedCupom.tipo === 'ORCAMENTO' || selectedCupom.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? 200.00 : undefined,
              observacoes: selectedCupom.tipo === 'ORDEM_SERVICO_ABERTURA' ? 'Equipamento com tela quebrada' : 'Garantia de 90 dias',
              dataHora: new Date().toLocaleString('pt-PT'),
              numeroDocumento: selectedCupom.tipo === 'ORDEM_SERVICO_ABERTURA' || selectedCupom.tipo === 'ORDEM_SERVICO_FECHAMENTO' ? 'OS-2024-001' :
                                selectedCupom.tipo === 'ORCAMENTO' ? 'ORC-2024-001' : 'REC-2024-001'
            }}
            onImprimir={() => {
              success('Cupom enviado para impressão')
              setShowPreviewModal(false)
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default CuponsTermicosPage